import { Helmet } from 'react-helmet-async'
import { Header } from '@/components/landing/Header'
import { Footer } from '@/components/landing/Footer'
import { toAbsoluteUrl } from '@/lib/seo'
import { Link } from 'react-router-dom'

const founders = [
	{
		name: 'Tarun Yadav',
		role: 'Co-founder',
		image: '/images/tarun-yadav.png',
		profile: 'https://x.com/tarunyadav9761',
	},
	{
		name: 'Pauline Clavelloux',
		role: 'Co-founder',
		image: '/images/pauline-clavelloux.png',
		profile: 'https://x.com/Pauline_Cx',
	},
]

const Team = () => {
	return (
		<div className="min-h-screen w-full mx-auto relative overflow-hidden bg-gradient-to-b from-gray-50 to-white">
			<Helmet>
				<title>Our Team | Feedbask</title>
				<meta
					name="description"
					content="Meet Tarun and Pauline, the co-founders of Feedbask, and discover the story behind the AI-powered customer support widget."
				/>
				<link rel="canonical" href={toAbsoluteUrl('/team')} />
				<meta property="og:title" content="Our Team | Feedbask" />
				<meta
					property="og:description"
					content="Meet Tarun and Pauline, the co-founders of Feedbask, and discover the story behind the AI-powered customer support widget."
				/>
				<meta property="og:url" content={toAbsoluteUrl('/team')} />
				<meta property="og:type" content="website" />
			</Helmet>

			<div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10">
				<div className="absolute top-24 left-10 w-64 h-64 rounded-full bg-[#866BFF] opacity-30 blur-3xl" />
				<div className="absolute bottom-32 right-10 w-72 h-72 rounded-full bg-indigo-100 opacity-30 blur-3xl" />
			</div>

			<div className="relative z-10 max-w-7xl px-4 mx-auto">
				<Header />

				<section className="py-16 md:py-24 text-center">
					<h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
						The team behind Feedbask
					</h1>
					<p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
						We are Tarun and Pauline, co-founders of Feedbask. We met on X,
						after years of working as freelancers and with large companies.
					</p>
				</section>

				<section className="grid md:grid-cols-2 gap-8 mb-16">
					{founders.map((founder) => (
						<article
							key={founder.name}
							className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow"
						>
							<div className="flex items-center gap-5">
								<img
									src={founder.image}
									alt={founder.name}
									className="w-20 h-20 rounded-full object-cover border-2 border-indigo-100"
								/>
								<div className="text-left">
									<h2 className="text-2xl font-semibold text-gray-900">
										{founder.name}
									</h2>
									<p className="text-gray-600">{founder.role}</p>
									<a
										href={founder.profile}
										target="_blank"
										rel="noopener noreferrer"
										className="text-[#866BFF] font-medium hover:underline"
									>
										View profile on X
									</a>
								</div>
							</div>
						</article>
					))}
				</section>

				<section className="bg-white rounded-2xl border border-gray-200 p-8 md:p-12 shadow-sm mb-20">
					<h2 className="text-3xl font-bold text-gray-900 mb-6">Why we built Feedbask</h2>
					<div className="space-y-5 text-gray-700 text-lg leading-relaxed">
						<p>
							The idea came from a recurring issue we faced on our other websites.
							Every time a client contacted us, we had to manually verify who they
							were, identify their account, and ask where the problem happened.
						</p>
						<p>
							We also noticed we were repeating the same answers again and again.
							It was inefficient, frustrating, and time-consuming for both us and
							our users.
						</p>
						<p>
							So we wanted a better way: a widget that captures user context
							automatically and helps generate AI responses based on previous
							answers and our documentation.
						</p>
						<p className="font-semibold text-gray-900">
							The result is Feedbask, <Link to="/">a customer support tool</Link>, and it saves us a huge amount of time.
						</p>
					</div>
				</section>

				<Footer />
			</div>
		</div>
	)
}

export default Team
